import { useEffect, useState } from "react";
import { BetSlipFragment } from "../../graphql/core-types";
import { decodeId } from "../../helpers";
import { calculatePotentialPayout, calculateSlipMultiplier, getBetOutcome, getFundingTotals, getPayoutAmounts } from "../../helpers/bet-line-helpers";
import { formatCurrency } from "../../helpers/currency";
import { useInternalization } from "../../context/cms-context";
import { GameIcon } from "../game-icon/game-icon";
import { GameModeIcon } from "../game-mode-icon/game-mode-icon";
import { useBetLegs } from "./use-bet-legs";
import { BetLegGroups } from "./bet-leg-groups";
import { CreditsUsedLabel, IncludesCredits } from "../bet-credits/bet-credits";
import { getCombinedTotals, getStakers } from "../../helpers/stake-helpers";
import { StakerAvatars } from "./staker-avatars";

// how long each footer view stays up before swapping, keep longer than the crossfade in bet-list.css
const FOOTER_CYCLE_MS = 6000

// flips between the streamer's own numbers and the totals with stakers, only while there are stakers to total
const useFooterCycle = (hasStakers: boolean) => {
  const [showCombined, setShowCombined] = useState(false)

  useEffect(() => {
    if (!hasStakers) {
      setShowCombined(false)
      return
    }
    const interval = setInterval(() => setShowCombined((current) => !current), FOOTER_CYCLE_MS)
    return () => clearInterval(interval)
  }, [hasStakers])

  return showCombined
}

const OUTCOME_LABEL = {
  pending: 'Pending',
  won: 'Won',
  lost: 'Lost',
  refunded: 'Refunded',
}

export const BetCardBody = ({ betSlip }: { betSlip: BetSlipFragment }) => {
  const { translate } = useInternalization()
  const { game, gameName, gameMode, gameModeSystemId, betTypeLabel, legGroups } = useBetLegs(betSlip)

  const outcome = getBetOutcome(betSlip)
  const isResolved = outcome !== 'pending'
  const multiplier = calculateSlipMultiplier(betSlip)
  const potentialPayout = calculatePotentialPayout(betSlip)
  // cash and credits are shown apart, the same way plc-app's bet details does
  const { plBalance, plCredits } = getFundingTotals(betSlip.betItems || [])
  const payoutAmounts = getPayoutAmounts(betSlip)
  const isFreeBet = plBalance === 0 && plCredits > 0
  const stakers = getStakers(betSlip)
  const combinedTotals = getCombinedTotals(betSlip, isResolved)
  const showCombined = useFooterCycle(stakers.length > 0)

  return (
    <div className="bet_list__body">
      <div className="bet_list__header">
        <div className="bet_list__title_group">
          <GameIcon systemId={gameName} size={32} />
          <div className="bet_list__title">
            <h2 className="bet_list__heading pl-fontL">{translate(`${game?.displayName || gameName}`)}</h2>
          </div>
        </div>
        <span className={`bet_list__status pl-fontXSContrast bet_list__status--${outcome}`}>
          {OUTCOME_LABEL[outcome]}
        </span>
      </div>

      {(gameMode?.displayName || betTypeLabel) && (
        <p className="bet_list__subheading pl-fontS">
          <GameModeIcon gameMode={gameModeSystemId} game={gameName} size={16} className="bet_list__game_mode_icon" />
          <span>{[gameMode?.displayName && translate(`${gameMode.displayName}`), betTypeLabel].filter(Boolean).join(' · ')}</span>
        </p>
      )}

      <BetLegGroups legGroups={legGroups} />

      {stakers.length > 0 && <StakerAvatars stakers={stakers} />}

      {/* both views sit in the same grid cell and crossfade, so the card keeps the taller one's height */}
      <div className="bet_list__footer_cycle">
        <div className={`bet_list__footer ${showCombined ? 'bet_list__footer--hidden' : ''}`} aria-hidden={showCombined}>
          <div className="bet_list__stat">
            <span className="bet_list__stat_label pl-fontXS">Wager</span>
            {/* a bet paid entirely with credits reads like a cash bet, showing its credit stake.
                otherwise the cash part, with any credits used listed underneath */}
            <span className="bet_list__stat_value pl-fontMContrast">{formatCurrency(String(isFreeBet ? plCredits : plBalance))}</span>
            {!isFreeBet && plCredits > 0 && <CreditsUsedLabel amount={plCredits} />}
          </div>
          {multiplier && (
            <div className="bet_list__stat">
              <span className="bet_list__stat_label pl-fontXS">Multiplier</span>
              <span className="bet_list__stat_value pl-fontMContrast">{multiplier}</span>
            </div>
          )}
          <div className="bet_list__stat">
            <span className="bet_list__stat_label pl-fontXS">{isResolved ? 'Payout' : 'To Win'}</span>
            <span className="bet_list__stat_value bet_list__stat_value--payout pl-fontMContrast">
              {formatCurrency(isResolved ? String(payoutAmounts.dollars + payoutAmounts.credits) : potentialPayout)}
            </span>
            {isResolved && payoutAmounts.credits > 0 && <IncludesCredits />}
          </div>
        </div>
        <div className={`bet_list__footer ${showCombined ? '' : 'bet_list__footer--hidden'}`} aria-hidden={!showCombined}>
          <div className="bet_list__stat">
            <span className="bet_list__stat_label pl-fontXS">Total Wager</span>
            <span className="bet_list__stat_value pl-fontMContrast">{formatCurrency(String(combinedTotals.wagered))}</span>
          </div>
          <div className="bet_list__stat">
            <span className="bet_list__stat_label pl-fontXS">Stakers</span>
            <span className="bet_list__stat_value pl-fontMContrast">{stakers.length}</span>
          </div>
          <div className="bet_list__stat">
            <span className="bet_list__stat_label pl-fontXS">Total Profit</span>
            {/* a settled bet can come out behind, shown as a red loss */}
            <span className={`bet_list__stat_value pl-fontMContrast ${combinedTotals.profit < 0 ? 'bet_list__stat_value--loss' : 'bet_list__stat_value--payout'}`}>
              {combinedTotals.profit < 0 ? '-' : '+'}{formatCurrency(String(Math.abs(combinedTotals.profit)))}
            </span>
          </div>
        </div>
      </div>

      <span className="bet_list__id pl-fontXS">#{decodeId(betSlip?.id || '')}</span>
    </div>
  )
}
